import { FontFamily } from "../../content-builder/element-schema";
import { BarcodeLabelSize } from "../barcode-label";

export namespace PaddingSize {
  export const SMALL = 4;
  export const MEDIUM = 8;
  export const LARGE = 16;
}

export enum BarcodeLabelFontWeight {
  NORMAL = "normal",
  BOLD = "bold",
}

export interface Padding {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

export enum AlignItems {
  LEFT = "Left",
  CENTER = "Center",
  RIGHT = "Right",
}

export const AlignItemsDisplayToCssValue: Record<AlignItems, string> = {
  [AlignItems.LEFT]: "flex-start",
  [AlignItems.CENTER]: "center",
  [AlignItems.RIGHT]: "flex-end",
};

export const AlignItemsToTextAlign: Record<
  AlignItems,
  "left" | "center" | "right"
> = {
  [AlignItems.LEFT]: "left",
  [AlignItems.CENTER]: "center",
  [AlignItems.RIGHT]: "right",
};

export const FONT_WEIGHTS = [
  BarcodeLabelFontWeight.NORMAL,
  BarcodeLabelFontWeight.BOLD,
] as const;

export const FONT_FAMILIES = [
  FontFamily.ARIAL,
  FontFamily.COURIER_NEW,
  FontFamily.GEORGIA,
  FontFamily.LUCIDA_SANS_UNICODE,
  FontFamily.TAHOMA,
  FontFamily.TIMES_NEW_ROMAN,
  FontFamily.TREBUCHET_MS,
  FontFamily.VERDANA,
] as const;

export enum BarcodeBarSource {
  SKU = "sku",
  UPC = "upc",
  PRODUCT_ID = "productId",
}

export enum ProductBarcodeAnnotation {
  NONE = "none",
  BARCODE_VALUE = "barcodeValue",
  SKU = "sku",
  UPC = "upc",
}

export enum BarcodeLabelBlockType {
  TEXT = "text",
  SPACER = "spacer",
  LINE = "line",
  PRODUCT_BARCODE = "product-barcode",
  PRODUCT_SKU = "product-sku",
  PRODUCT_TITLE = "product-title",
  PRODUCT_PRICE = "product-price",
  PRODUCT_IMAGE = "product-image",
  PRODUCT_ATTRIBUTE = "product-attribute",
}

// fieldKey on a ProductAttribute block is the attributeDefinitionId of a
// merchant-defined product attribute (matches the inventory levels filter).

export enum BarcodeLabelBlockCategory {
  GENERAL = "general",
  PRODUCT = "product",
}

export namespace Section {
  export interface BaseSection {
    blockId: string;
    sectionPadding: Padding;
    sectionColor?: string | null;
    alignSelf?: AlignItems | null;
  }

  export interface FontSection extends BaseSection {
    fontFamily?: string | null;
    fontSize?: number | null;
    textColor?: string | null;
    fontWeight?: BarcodeLabelFontWeight | null;
  }

  export interface Text extends BaseSection {
    type: BarcodeLabelBlockType.TEXT;
    textColor: string;
    fontSize: number;
    fontFamily: string;
    text: string;
  }

  export interface Spacer extends BaseSection {
    type: BarcodeLabelBlockType.SPACER;
    height: number;
  }

  export interface Line extends BaseSection {
    type: BarcodeLabelBlockType.LINE;
    color: string;
  }

  export interface ProductBarcode extends BaseSection {
    type: BarcodeLabelBlockType.PRODUCT_BARCODE;
    source: BarcodeBarSource;
    annotation: ProductBarcodeAnnotation;
    annotationFontSize?: number | null;
    annotationFontWeight?: BarcodeLabelFontWeight | null;
    width?: number | null;
    height?: number | null;
  }

  export interface ProductSku extends FontSection {
    type: BarcodeLabelBlockType.PRODUCT_SKU;
  }

  export interface ProductTitle extends FontSection {
    type: BarcodeLabelBlockType.PRODUCT_TITLE;
    maxLines?: number | null;
  }

  export interface ProductPrice extends FontSection {
    type: BarcodeLabelBlockType.PRODUCT_PRICE;
    showCurrencyCode?: boolean | null;
  }

  export interface ProductImage extends BaseSection {
    type: BarcodeLabelBlockType.PRODUCT_IMAGE;
    maxHeight?: number | null;
  }

  export interface ProductAttribute extends FontSection {
    type: BarcodeLabelBlockType.PRODUCT_ATTRIBUTE;
    // attribute_definition.name (e.g. "Handle"). Resolved server-side via
    // product_attribute2 / product_family_attribute2.
    fieldKey: string;
    // Optional prefix shown before the value (e.g. "Color: ").
    label?: string | null;
  }
}

export type Section =
  | Section.Text
  | Section.Spacer
  | Section.Line
  | Section.ProductBarcode
  | Section.ProductSku
  | Section.ProductTitle
  | Section.ProductPrice
  | Section.ProductImage
  | Section.ProductAttribute;

export enum PageOrientation {
  PORTRAIT = "portrait",
  LANDSCAPE = "landscape",
}

export interface BarcodeLabelPageLayout {
  size: BarcodeLabelSize;
  orientation: PageOrientation;
}

export function getPageDimensions(layout: BarcodeLabelPageLayout): {
  width: number;
  height: number;
} {
  const [w, h] = layout.size.split("x").map(Number);
  if (layout.orientation === PageOrientation.LANDSCAPE) {
    return { width: h!, height: w! };
  }
  return { width: w!, height: h! };
}
